import { helpFor } from "../metric-help.js";
import { Breadcrumb } from "../shell/Shell.js";
import { humanizeReason } from "../format.js";
import { useApi } from "../api/useApi.js";
import type { ProvenanceSummary } from "../api/client.js";
import {
  RagDot,
  Spine,
  spineLevels,
  ConfidencePill,
  Basis,
  basisKindOf,
  ProvenancePopover,
  EmptyState,
  type Rag,
  HelpTip,
} from "../components.js";
import "./portfolio.css";

/**
 * Portfolio screen — built to the locked demo (portfolio.html): Needs-attention panel, program
 * rollup with sub-projects, and standalone project cards, over the live /api/v1/portfolio. Honest
 * about missing evidence: the API supplies status, confidence and the program's blended forecast.
 * Every figure carries its provenance; the API role-projects what each role may see.
 */

interface PortfolioProject {
  readonly id: string;
  readonly name: string;
  readonly scopeType: string;
  readonly parentProjectId: string | null;
  readonly status: string;
  readonly confidence: number | null;
  readonly capped: boolean;
  readonly reason: string;
  readonly provenance: ProvenanceSummary | null;
  /** NX-2800 — declared confidence basis (null = no profile declared, honest N/A). */
  readonly confidenceBasis: string | null;
  /** NX-2800 — ADR-016 credibility flag; the same one the status cap uses. */
  readonly hasDeliveryBasis: boolean;
  readonly forecast?:
    | { kind: "n/a"; reason: string }
    | {
        kind: "value";
        value: { p50: number; p85: number };
        confidence: { kind: "n/a" } | { kind: "value"; confidence: number };
        lineage: number[];
      };
}

const STATUS_RAG: Record<string, Rag> = {
  green: "green",
  yellow: "amber",
  red: "red",
  unsupported: "neutral",
};
const STATUS_LABEL: Record<string, string> = {
  green: "On track",
  yellow: "At risk",
  red: "Off track",
  unsupported: "Unsupported",
};
const ragOf = (status: string): Rag => STATUS_RAG[status] ?? "neutral";
const labelOf = (status: string): string => STATUS_LABEL[status] ?? status;

/**
 * Provenance drill for a single project/program health figure (E8.3-AC3) — no figure is a naked
 * number. It exposes that project's own source/method/confidence; when the project carries no
 * provenance summary, it still drills to the project's confidence rather than reading bare.
 */
function FigureProvenance({ project }: { project: PortfolioProject }): React.ReactNode {
  const prov = project.provenance;
  return (
    <ProvenancePopover
      source={prov?.source ?? "—"}
      method={prov?.method ?? "evidence-derived status"}
      confidence={
        project.scopeType === "program"
          ? project.confidence
          : (prov?.confidence ?? project.confidence)
      }
    >
      provenance
    </ProvenancePopover>
  );
}

export function Portfolio(): React.ReactNode {
  const state = useApi<PortfolioProject[]>("/portfolio");
  return (
    <>
      <Breadcrumb items={[{ label: "Portfolio" }]} />
      <div className="head">
        <h1>Portfolio</h1>
      </div>
      {state.status === "loading" && (
        <div className="meta">Loading the portfolio from the evidence store…</div>
      )}
      {state.status === "error" && (
        <EmptyState message={`Couldn't load the portfolio: ${state.error.message}`} />
      )}
      {state.status === "ready" && (
        <PortfolioBody
          projects={state.envelope.data}
          dataConfidence={Math.round(state.envelope.meta.confidence * 100)}
          asOf={state.envelope.meta.asOf}
          provenance={state.envelope.meta.provenance[0] ?? null}
        />
      )}
    </>
  );
}

export interface PortfolioBodyProps {
  projects: PortfolioProject[];
  dataConfidence: number;
  asOf: string | null;
  provenance: ProvenanceSummary | null;
}

export function PortfolioBody({
  projects,
  dataConfidence,
  asOf,
  provenance,
}: PortfolioBodyProps): React.ReactNode {
  if (projects.length === 0) {
    return (
      <EmptyState message="No projects yet — connect a source to begin collecting evidence." />
    );
  }

  const programs = projects.filter((p) => p.scopeType === "program");
  const childrenOf = (id: string): PortfolioProject[] =>
    projects.filter((p) => p.parentProjectId === id);
  const standalones = projects.filter((p) => p.scopeType !== "program" && !p.parentProjectId);

  // Guarantee every API-returned project is rendered. The /portfolio list is flat and paginated by
  // project_id, so a child can arrive without its parent program on the same page — it is neither a
  // program, a standalone, nor a child of a *visible* program, so it would otherwise vanish. Collect
  // everything already shown above and render the remainder in a fallback section.
  const rendered = new Set<string>();
  programs.forEach((prog) => {
    rendered.add(prog.id);
    childrenOf(prog.id).forEach((c) => rendered.add(c.id));
  });
  standalones.forEach((p) => rendered.add(p.id));
  const orphans = projects.filter((p) => !rendered.has(p.id));

  // Needs-attention is presented as "sorted by severity": red (off track) before yellow (at risk).
  // The API returns project_id order, so sort here rather than trust arrival order.
  const SEVERITY_RANK: Record<string, number> = { red: 0, yellow: 1 };
  const attention = projects
    .filter((p) => p.status === "red" || p.status === "yellow")
    .sort((a, b) => (SEVERITY_RANK[a.status] ?? 99) - (SEVERITY_RANK[b.status] ?? 99));
  const dataRag: Rag = dataConfidence >= 75 ? "green" : dataConfidence >= 50 ? "amber" : "red";

  return (
    <>
      <div className="meta">
        As of {asOf ?? "now"}{" "}
        <span className="dataconf">
          <RagDot status={dataRag} label="portfolio data confidence" /> data confidence{" "}
          <b>{dataConfidence}%</b> ·{" "}
          <ProvenancePopover
            source={provenance?.source ?? "—"}
            method={provenance?.method ?? "—"}
            confidence={dataConfidence / 100}
          >
            provenance
          </ProvenancePopover>
        </span>
        <HelpTip text={helpFor("Data confidence")!} label="data confidence" />
      </div>

      {attention.length > 0 && (
        <div className="panel attn enter">
          <div className="ah">
            <span className="pulse" aria-hidden="true" />
            <span className="lab" style={{ color: "var(--neg)" }}>
              Needs attention
            </span>
          </div>
          <div className="sub">
            {attention.length} signal{attention.length === 1 ? "" : "s"} surfaced from the evidence
            — sorted by severity, each links to its provenance.
          </div>
          {attention.map((p) => (
            <a key={p.id} className="attnrow" href={`/projects/${p.id}`}>
              <RagDot status={ragOf(p.status)} label={labelOf(p.status)} />
              <span className="who">{p.name}</span>
              <span className="what">{humanizeReason(p.reason)}</span>
              <span className="go">View →</span>
            </a>
          ))}
        </div>
      )}

      {programs.map((prog) => {
        const kids = childrenOf(prog.id);
        return (
          <div key={prog.id}>
            <div className="sectitle">
              <h3 className="sec">Program rollup</h3>
            </div>
            <div className="panel pad enter">
              <div className="proghead">
                <Spine levels={spineLevels(ragOf(prog.status))} label={labelOf(prog.status)} />
                <h2>
                  <a href={`/projects/${prog.id}`}>{prog.name}</a>
                </h2>
                <span className="kind">
                  Program · {kids.length} sub-project{kids.length === 1 ? "" : "s"}
                </span>
              </div>
              <p className="progsum">{humanizeReason(prog.reason)}</p>
              <div className="progmeta">
                <span className="kind">
                  {prog.hasDeliveryBasis
                    ? "Delivery basis from sub-projects"
                    : "No sub-project delivery basis"}
                </span>
                <div className="fc">
                  <span className="lab">
                    Rolled-up forecast
                    <HelpTip text={helpFor("Rolled-up forecast")!} label="Rolled-up forecast" />
                  </span>
                  <span className="fv">
                    {prog.forecast?.kind === "value" ? (
                      <>
                        {Number(prog.forecast.value.p50.toFixed(1))} periods (P50){" "}
                        <small>
                          {Number(prog.forecast.value.p85.toFixed(1))} periods (P85), blended from
                          sub-project evidence
                        </small>
                      </>
                    ) : (
                      <>
                        No data yet{" "}
                        <small>
                          {humanizeReason(
                            prog.forecast?.reason ?? "no sub-project forecast available",
                          )}
                        </small>
                      </>
                    )}
                  </span>
                </div>
                <span className="sp">
                  <ConfidencePill value={prog.confidence} />
                  <FigureProvenance project={prog} />
                </span>
              </div>
              {kids.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  {kids.map((c) => (
                    <a key={c.id} className="childrow" href={`/projects/${c.id}`}>
                      <RagDot status={ragOf(c.status)} label={labelOf(c.status)} />
                      <div className="childinfo">
                        <div className="cn">{c.name}</div>
                        <div className="ck">
                          {c.scopeType} · {labelOf(c.status)}
                        </div>
                      </div>
                      <ConfidencePill value={c.confidence} />
                      <FigureProvenance project={c} />
                      <div className="childoutcome">
                        <span className="head2">{humanizeReason(c.reason)}</span>
                        <span className="arrow" aria-hidden="true">
                          →
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {standalones.length > 0 && (
        <>
          <div className="sectitle">
            <h3 className="sec">Standalone projects</h3>
            <span className="cnt">not part of any program · each its own delivery basis</span>
          </div>
          <div className="grid cols-3">
            {standalones.map((p) => (
              <a
                key={p.id}
                className={`card pcard enter${p.status === "red" ? " danger" : ""}`}
                href={`/projects/${p.id}`}
              >
                <div className="top">
                  <Spine levels={spineLevels(ragOf(p.status))} label={labelOf(p.status)} />
                  <div style={{ flex: 1 }}>
                    <div className="nm">{p.name}</div>
                  </div>
                </div>
                <Basis kind={basisKindOf(p.confidenceBasis)} />
                <div className="headline">{humanizeReason(p.reason)}</div>
                <div className="foot">
                  <ConfidencePill value={p.confidence} />
                  <FigureProvenance project={p} />
                  <span className="open">Open project →</span>
                </div>
              </a>
            ))}
          </div>
        </>
      )}

      {orphans.length > 0 && (
        <>
          <div className="sectitle">
            <h3 className="sec">Other projects</h3>
            <span className="cnt">parent program not on this page · still tracked here</span>
          </div>
          <div className="grid cols-3">
            {orphans.map((p) => (
              <a
                key={p.id}
                className={`card pcard enter${p.status === "red" ? " danger" : ""}`}
                href={`/projects/${p.id}`}
              >
                <div className="top">
                  <Spine levels={spineLevels(ragOf(p.status))} label={labelOf(p.status)} />
                  <div style={{ flex: 1 }}>
                    <div className="nm">{p.name}</div>
                  </div>
                </div>
                <Basis kind={basisKindOf(p.confidenceBasis)} />
                <div className="headline">{humanizeReason(p.reason)}</div>
                <div className="foot">
                  <ConfidencePill value={p.confidence} />
                  <FigureProvenance project={p} />
                  <span className="open">Open project →</span>
                </div>
              </a>
            ))}
          </div>
        </>
      )}
    </>
  );
}
